import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
  Snackbar,
  TablePagination,
  Checkbox,
  Menu,
  MenuItem,
  Chip,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { 
  fetchPartsStart, 
  deletePart, 
  clearError,
  setSelectedPart 
} from '../../store/reducers/partsSlice';
import { Part } from '../../services/partsApi';

const PartsList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { parts, loading, error, total, page, limit } = useSelector((state: RootState) => state.parts || { parts: [], loading: false, error: null, total: 0, page: 1, limit: 10 });
  const safeParts: Part[] = Array.isArray(parts) ? parts : [];
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [selectedParts, setSelectedParts] = useState<string[]>([]);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    dispatch(fetchPartsStart({ page: 1, limit: 10 }));
  }, [dispatch]);

  useEffect(() => {
    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      if (searchTerm) {
        dispatch(fetchPartsStart({ page: 1, limit: 10, search: searchTerm }));
      } else {
        dispatch(fetchPartsStart({ page: 1, limit: 10 }));
      }
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [searchTerm, dispatch]);

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(fetchPartsStart({ page: newPage + 1, limit }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(fetchPartsStart({ page: 1, limit: parseInt(event.target.value, 10) }));
  };

  const handleAddNew = () => {
    navigate('/parts/new');
  };

  const handleEdit = (part: Part) => {
    dispatch(setSelectedPart(part));
    navigate(`/parts/edit/${part.id}`);
  };

  const handleDelete = (part: Part) => {
    const confirmMessage = `Are you sure you want to delete part "${part.part_num}"?\n\nThis action cannot be undone.`;
    if (window.confirm(confirmMessage)) {
      dispatch(deletePart(part.id!));
    }
  };

  const handleView = (part: Part) => {
    dispatch(setSelectedPart(part));
    navigate(`/parts/view/${part.id}`);
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  // Bulk operations
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedParts(safeParts.map(part => part.id!));
    } else {
      setSelectedParts([]);
    }
  };

  const handleSelectPart = (partId: string) => {
    setSelectedParts(prev => 
      prev.includes(partId) 
        ? prev.filter(id => id !== partId)
        : [...prev, partId]
    );
  };

  const handleBulkMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setBulkMenuAnchor(event.currentTarget);
  };

  const handleBulkMenuClose = () => {
    setBulkMenuAnchor(null);
  };

  const handleBulkActivate = () => {
    if (selectedParts.length === 0) return;
    
    const confirmMessage = `Are you sure you want to activate ${selectedParts.length} part(s)?`;
    if (window.confirm(confirmMessage)) {
      // TODO: Implement bulk activate API call
      console.log('Bulk activate parts:', selectedParts);
      setSelectedParts([]);
      handleBulkMenuClose();
    }
  };

  const handleBulkDeactivate = () => {
    if (selectedParts.length === 0) return;
    
    const confirmMessage = `Are you sure you want to deactivate ${selectedParts.length} part(s)?`;
    if (window.confirm(confirmMessage)) {
      // TODO: Implement bulk deactivate API call
      console.log('Bulk deactivate parts:', selectedParts);
      setSelectedParts([]);
      handleBulkMenuClose();
    }
  };

  const handleBulkDelete = () => {
    if (selectedParts.length === 0) return;
    
    const confirmMessage = `Are you sure you want to delete ${selectedParts.length} part(s)?\n\nThis action cannot be undone.`;
    if (window.confirm(confirmMessage)) {
      selectedParts.forEach(partId => {
        dispatch(deletePart(partId));
      });
      setSelectedParts([]);
      handleBulkMenuClose();
    }
  };

  if (loading && safeParts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Parts
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add New Part
        </Button>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message={error}
      />

      <Paper>
        <Box p={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search parts by number or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Bulk Operations Toolbar */}
        {selectedParts.length > 0 && (
          <Box p={2} bgcolor="primary.light" color="primary.contrastText">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Chip 
                  label={`${selectedParts.length} selected`} 
                  color="primary" 
                  variant="outlined"
                  style={{ color: 'white', borderColor: 'white' }}
                />
                <Button
                  color="inherit"
                  onClick={handleBulkMenuOpen}
                  startIcon={<MoreVertIcon />}
                >
                  Bulk Actions
                </Button>
              </Box>
              <Button
                color="inherit"
                onClick={() => setSelectedParts([])}
              >
                Clear Selection
              </Button>
            </Box>
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedParts.length > 0 && selectedParts.length < safeParts.length}
                    checked={safeParts.length > 0 && selectedParts.length === safeParts.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Part Number</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Issue</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Classification</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Part Status</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {safeParts.map((part) => (
                <TableRow key={part.id} selected={selectedParts.includes(part.id!)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedParts.includes(part.id!)}
                      onChange={() => handleSelectPart(part.id!)}
                    />
                  </TableCell>
                  <TableCell>{part.part_num}</TableCell>
                  <TableCell>{part.description}</TableCell>
                  <TableCell>{part.part_issue}</TableCell>
                  <TableCell>{part.client?.name || part.client_id}</TableCell>
                  <TableCell>{part.part_classification_id}</TableCell>
                  <TableCell>${part.price_per_unit?.toFixed(2) || '0.00'}</TableCell>
                  <TableCell>{part.status?.status_name || part.part_status_id}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={part.is_active ? 'primary' : 'error'}
                    >
                      {part.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => handleView(part)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(part)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(part)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={limit}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Bulk Operations Menu */}
      <Menu
        anchorEl={bulkMenuAnchor}
        open={Boolean(bulkMenuAnchor)}
        onClose={handleBulkMenuClose}
      >
        <MenuItem onClick={handleBulkActivate}>
          <CheckCircleIcon style={{ marginRight: 8 }} />
          Activate Selected
        </MenuItem>
        <MenuItem onClick={handleBulkDeactivate}>
          <CancelIcon style={{ marginRight: 8 }} />
          Deactivate Selected
        </MenuItem>
        <MenuItem onClick={handleBulkDelete} style={{ color: 'red' }}>
          <DeleteIcon style={{ marginRight: 8 }} />
          Delete Selected
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PartsList; 